import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import colors from 'hsl-shared-components/lib/Theme/colors';
import cardReaderImg from '../../assets/images/card-reader-transparent.png';
import cardReaderLightImg from '../../assets/images/card-reader-with-light-transparent.png';
import hslLogoImg from '../../assets/images/hsl-logo.png';
import { getCurrentTimeString } from '../../time';
import {
  TINY_BREAKPOINT_IN_PX,
  TINY_SIZE_IN_PERCENTAGE,
  MOBILE_BREAKPOINT_IN_PX,
  MOBILE_SIZE_IN_PERCENTAGE,
  TABLET_BREAKPOINT_IN_PX,
  TABLET_SIZE_IN_PERCENTAGE,
  CARD_READER_ZOOM_1_IN_PERCENTAGE,
  CARD_READER_ZOOM_2_IN_PERCENTAGE,
  CARD_READER_ZOOM_1_MOBILE_IN_PERCENTAGE,
  CARD_READER_ZOOM_2_MOBILE_IN_PERCENTAGE,
} from '../../constants';

const DEVICE_HEIGHT_IN_PX = 1142;
const DEVICE_WIDTH_IN_PX = 632;
const SCREEN_HEIGHT_IN_PX = 240;
const SCREEN_WIDTH_IN_PX = 400;

export const getRelativeHeight = (pixels) => `${pixels / SCREEN_HEIGHT_IN_PX * 100}%`; // eslint-disable-line
export const getRelativeWidth = (pixels) => `${pixels / SCREEN_WIDTH_IN_PX * 100}%`; // eslint-disable-line
export const getScale = (zoomLevel) => (zoomLevel === 1 ? CARD_READER_ZOOM_1_IN_PERCENTAGE : CARD_READER_ZOOM_2_IN_PERCENTAGE); // eslint-disable-line
export const getScaleSize = (zoomLevel, type) => {
  if (type === 'tablet') {
    return zoomLevel === 1
      ? CARD_READER_ZOOM_1_IN_PERCENTAGE
      : CARD_READER_ZOOM_2_IN_PERCENTAGE;
  }
  if (type === 'mobile') {
    return zoomLevel === 1
      ? CARD_READER_ZOOM_1_MOBILE_IN_PERCENTAGE
      : CARD_READER_ZOOM_2_MOBILE_IN_PERCENTAGE;
  }
  return CARD_READER_ZOOM_1_IN_PERCENTAGE;
};

const getCardReaderPosition = (isHomeScreen, zoomLevel) => {
  if (isHomeScreen) {
    return '0';
  }

  if (!isHomeScreen && zoomLevel === 2) {
    return '-40';
  }

  return '0';
};

export const getFontSize = (zoomLevel, type) => {
  // prettier-ignore
  const baseSize = zoomLevel === 1
    ? CARD_READER_ZOOM_1_IN_PERCENTAGE
    : CARD_READER_ZOOM_2_IN_PERCENTAGE;
  if (type === 'tablet') {
    return `${baseSize * TABLET_SIZE_IN_PERCENTAGE}em`;
  }
  if (type === 'mobile') {
    return `${baseSize * MOBILE_SIZE_IN_PERCENTAGE}em`;
  }
  return `${baseSize}em`;
};

// prettier-ignore
const DeviceWrapper = styled.div`
  height: ${DEVICE_HEIGHT_IN_PX}px;
  width: ${DEVICE_WIDTH_IN_PX}px;
  min-width: ${DEVICE_WIDTH_IN_PX}px;
  transition: transform 0.5s ease-in-out;
  transform-origin: top left;
  display: flex;
  justify-content: center;

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    height: ${DEVICE_HEIGHT_IN_PX * TABLET_SIZE_IN_PERCENTAGE}px;
    width: ${DEVICE_WIDTH_IN_PX * TABLET_SIZE_IN_PERCENTAGE}px;
    min-width: ${DEVICE_WIDTH_IN_PX * TABLET_SIZE_IN_PERCENTAGE}px;
    transform: scale(${(props) => getScaleSize(props.zoom, 'tablet')}) !important;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    height: ${DEVICE_HEIGHT_IN_PX * MOBILE_SIZE_IN_PERCENTAGE}px;
    width: ${DEVICE_WIDTH_IN_PX * MOBILE_SIZE_IN_PERCENTAGE}px;
    min-width: ${DEVICE_WIDTH_IN_PX * MOBILE_SIZE_IN_PERCENTAGE}px;
    transform-origin: top center;
    transform: scale(${(props) => getScaleSize(props.zoom, 'mobile')}) !important;
    position: relative;
    top: ${(props) => getCardReaderPosition(props.isHomeScreen, props.zoom)}px;
  }

  @media screen and (max-width: ${TINY_BREAKPOINT_IN_PX}px) {
    height: ${DEVICE_HEIGHT_IN_PX * TINY_SIZE_IN_PERCENTAGE}px;
    width: ${DEVICE_WIDTH_IN_PX * TINY_SIZE_IN_PERCENTAGE}px;
    min-width: ${DEVICE_WIDTH_IN_PX * TINY_SIZE_IN_PERCENTAGE}px;
    transform: scale(${(props) => getScaleSize(props.zoom, 'mobile')}) !important;
  }

  ${(props) => props.isFullScreenModeEnabled && css`
    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
      height: ${DEVICE_HEIGHT_IN_PX}px !important;
      width: ${DEVICE_WIDTH_IN_PX}px !important;
      min-width: ${DEVICE_WIDTH_IN_PX}px !important;
    }
  `};
`;

// TODO: use a separate layer for device shade and remove `pointer-events: none`
const Device = styled.img`
  height: 100%;
  min-width: 100%;
  position: relative;
  pointer-events: none;
  user-select: none;
  display: flex;
  justify-content: center;
`;

// prettier-ignore
const Screen = styled.div`
  background-color: #222222;
  color: ${colors.primary.hslWhite};
  position: absolute;
  top: 25.5%;
  right: 18.5%;
  bottom: 53.7%;
  left: 18.9%;
  font-size: ${(props) => getFontSize(props.zoomLevel)};

  @media screen and (max-width: ${TABLET_BREAKPOINT_IN_PX}px) {
    left: 18.8%;
    font-size: ${(props) => getFontSize(props.zoomLevel, 'tablet')};
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    bottom: 53.6%;
    font-size: ${(props) => getFontSize(props.zoomLevel, 'mobile')};
  }

  ${(props) => props.isFullScreenModeEnabled && css`
    @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
      font-size: ${getFontSize(props.zoomLevel)} !important;
    }
  `};
`;

const HSLLogo = styled.img`
  position: absolute;
  top: ${getRelativeHeight(9)};
  left: ${getRelativeWidth(10)};
  width: ${getRelativeWidth(55)};
  height: auto;
  z-index: 3;

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    top: ${getRelativeHeight(6)};
  }
`;

// prettier-ignore
const CurrentTime = styled.div`
  position: absolute;
  top: ${getRelativeHeight(9)};
  right: ${getRelativeWidth(10)};
  font-size: 1em;
  font-weight: 400;
  letter-spacing: 0;
  color: ${colors.primary.hslWhite};
  z-index: 3;

  @media screen and (max-width: ${MOBILE_BREAKPOINT_IN_PX}px) {
    top: ${getRelativeHeight(6)};
  }
`;

const CardReader = ({
  isHomeScreen,
  zoomLevel,
  greenLightActive,
  children,
  isFullScreenModeEnabled,
}) => {
  const deviceScaleStyle = {
    transform: `scale(${getScale(zoomLevel)})`,
  };
  return (
    <DeviceWrapper
      isHomeScreen={isHomeScreen}
      zoom={zoomLevel}
      style={deviceScaleStyle}
      isFullScreenModeEnabled={isFullScreenModeEnabled}
    >
      <Screen isFullScreenModeEnabled={isFullScreenModeEnabled}>
        {children}
        <HSLLogo src={hslLogoImg} />
        <CurrentTime zoomLevel={zoomLevel}>
          {getCurrentTimeString()}
        </CurrentTime>
      </Screen>
      <Device src={greenLightActive ? cardReaderLightImg : cardReaderImg} />
    </DeviceWrapper>
  );
};

CardReader.propTypes = {
  isHomeScreen: PropTypes.bool.isRequired,
  zoomLevel: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  greenLightActive: PropTypes.bool.isRequired,
  isFullScreenModeEnabled: PropTypes.bool.isRequired,
};

export default CardReader;
